import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { toast } from "sonner";
import { usePlatformStore } from "@platform/store";
import {
  AudioVideoStartFailed,
  AudioVideoStartTimeout,
  DeviceStartFailureHandled,
  joinChimeSession,
  leaveChimeSession,
  stopChimeStreams,
  VIDEO_QUALITY,
  VIDEO_QUALITY_RELAXED,
  videoEventBus,
} from "@core/features/video";
import type { MeetingMode } from "@core/features/video";
import {
  joinVideoMeetingApi,
  leaveVideoMeeting as leaveVideoMeetingApi,
  performVideoLeave,
} from "@core/features/videoMeeting";
import type { MeetingDetails } from "@core/features/videoMeeting";
import { logMeetingEvent } from "@core/features/videoMeeting/utils/logMeetingEvent";
import { useDevices } from "@platform/features/video/providers/DevicesProvider";
import {
  getDeviceStartErrorGuidance,
  type DeviceStartFailure,
} from "../utils/getDeviceStartErrorGuidance";

// Concurrent-call dedup for clearExistingSession. Two simultaneous mounts
// of VideoJoinPage (parent re-render race, React StrictMode in dev, future
// additional callers) would otherwise both read sessionStorage before
// either clears it and produce duplicate same-second leave POSTs. Singleton
// promise — once a clear is in-flight, additional calls await the same one.
// Cleared on settle so a subsequent (sequential) call can run normally; a
// successful clear's removal of sessionStorage makes that sequential call a
// no-op via the existing `if (!effectiveRoomId && !effectiveMeetingId)` guard.
let inFlightClear: Promise<void> | null = null;

async function validateSelectedDeviceIds(
  audioId: string | null | undefined,
  videoId: string | null | undefined,
  audioOutputId: string | null | undefined
): Promise<{ audio: string | null; video: string | null; audioOutput: string | null }> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) {
    return {
      audio: audioId ?? null,
      video: videoId ?? null,
      audioOutput: audioOutputId ?? null,
    };
  }
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioIds = new Set(
      devices.filter((d) => d.kind === "audioinput").map((d) => d.deviceId)
    );
    const videoIds = new Set(
      devices.filter((d) => d.kind === "videoinput").map((d) => d.deviceId)
    );
    const outIds = new Set(
      devices.filter((d) => d.kind === "audiooutput").map((d) => d.deviceId)
    );
    return {
      audio: audioId && audioIds.has(audioId) ? audioId : null,
      video: videoId && videoIds.has(videoId) ? videoId : null,
      audioOutput: audioOutputId && outIds.has(audioOutputId) ? audioOutputId : null,
    };
  } catch {
    return {
      audio: audioId ?? null,
      video: videoId ?? null,
      audioOutput: audioOutputId ?? null,
    };
  }
}

type SessionErrorDetail = { title: string; causes: string[] } | null;

type UseJoinVideoMeetingArgs = {
  meetingId: string | null;
  roomId: number | null;
  meetingDetails: MeetingDetails | null;
  region: string;
  enableSimulcast: boolean;
  priorityBasedPolicy?: any;
  keepLastFrameWhenPaused: boolean;
  isEchoReductionEnabled: boolean;
  // Store setters
  setMeetingId: (id: string | null) => void;
  setRoomId: (id: number | null) => void;
  setInitialized: (initialized: boolean) => void;
  setRegion: (region: string) => void;
  setMeetingMode: (mode: MeetingMode) => void;
  setSessionError: (error: string | null, detail?: SessionErrorDetail) => void;
  setRoomMembersData: (data: any) => void;
  setChimeExternalUserId: (id: string | null) => void;
  setUserName: (name: string | null) => void;
  resetVideo: () => void;
  resetVideoMeeting: () => void;
};

export function useJoinVideoMeeting({
  meetingId,
  roomId,
  meetingDetails,
  region,
  enableSimulcast,
  priorityBasedPolicy,
  keepLastFrameWhenPaused,
  isEchoReductionEnabled,
  setMeetingId,
  setRoomId,
  setInitialized,
  setRegion,
  setMeetingMode,
  setSessionError,
  setRoomMembersData,
  setChimeExternalUserId,
  setUserName,
  resetVideo,
  resetVideoMeeting,
}: UseJoinVideoMeetingArgs) {
  const meetingManager = useMeetingManager();
  const navigateRef = useRef(useNavigate());
  const user = usePlatformStore((s) => s.user);
  const chimeExternalUserId = usePlatformStore((s) => s.chimeExternalUserId);
  const selectedAudioInputDevice = usePlatformStore((s) => s.selectedAudioInputDevice);
  const selectedVideoInputDevice = usePlatformStore((s) => s.selectedVideoInputDevice);
  const selectedAudioOutputDevice = usePlatformStore((s) => s.selectedAudioOutputDevice);
  const {
    audioInputDevices,
    videoInputDevices,
    audioOutputDevices,
  } = useDevices();
  const audioInputDevicesRef = useRef(audioInputDevices);
  audioInputDevicesRef.current = audioInputDevices;
  const videoInputDevicesRef = useRef(videoInputDevices);
  videoInputDevicesRef.current = videoInputDevices;
  const audioOutputDevicesRef = useRef(audioOutputDevices);
  audioOutputDevicesRef.current = audioOutputDevices;

  // Join meeting: call API + Chime session + set devices
  const joinMeeting = useCallback(async () => {
    try {
      // Atomic clear of both sessionError and sessionErrorDetail so a retry
      // doesn't carry stale device-failure causes into a fresh attempt.
      setSessionError(null, null);

      if (!meetingDetails) {
        throw new Error("No meeting details available");
      }

      if (!meetingDetails.video_rooms?.[0]) {
        throw new Error("No video room available for this meeting");
      }

      // Call backend join API
      const joinResponse = await joinVideoMeetingApi(
        {
          meeting_id: meetingDetails.id,
          region,
          echo_reduction: isEchoReductionEnabled,
        },
        user ? { id: user.id } : null
      );

      const chimeMeetingId = joinResponse.chime_meeting_id;

      // Join Chime session.
      //
      // joinChimeSession now awaits audioVideoDidStart before returning, so
      // the SDK's WebRTC handshake is complete by the time it resolves. The
      // previous code relied on a blind 500ms setTimeout here as a proxy for
      // handshake completion; that approximation broke on slower handshakes
      // (Safari 26 / Chrome 147 on macOS Sequoia/Tahoe), causing renegotiation
      // collisions during device init that left the local preview rendering
      // dark green / black with no telemetry. Do not re-add a setTimeout
      // here. See docs/superpowers/plans/2026-05-04-video-webrtc-handshake-race-fix.md.
      try {
        await joinChimeSession({
          joinInfo: joinResponse.join_info,
          enableSimulcast,
          priorityBasedPolicy,
          keepLastFrameWhenPaused,
          meetingManager,
          setRegion,
          setMeetingMode,
        });
      } catch (err) {
        // joinResponse + meetingDetails + user are in scope here, so
        // joinChimeSession stays a clean transport-level helper that doesn't
        // know about Django meeting IDs or PollinatorUser — telemetry lives
        // in the consumer.
        if (err instanceof AudioVideoStartTimeout) {
          logMeetingEvent({
            event_type: 'sdk_audioVideoDidStart_timeout',
            timeout_ms: err.timeoutMs,
            meeting_id: meetingDetails.id,
            room_id: joinResponse.room_id,
            user: user ? { id: user.id } : null,
          });
        } else if (err instanceof AudioVideoStartFailed) {
          logMeetingEvent({
            event_type: 'sdk_audioVideoDidStart_failed',
            status_code: err.statusCode,
            meeting_id: meetingDetails.id,
            room_id: joinResponse.room_id,
            user: user ? { id: user.id } : null,
          });
        }
        throw err;
      }

      // Use joinResponse data directly — store isn't updated yet at this point
      const deviceEventCtx = () => ({
        meeting_id: meetingDetails.id,
        room_id: joinResponse.room_id,
        user: user ? { id: user.id } : null,
      });

      // Build device label lookup from DevicesProvider refs (always current)
      const deviceLabelMap: Record<string, string> = {};
      for (const d of [...audioInputDevicesRef.current, ...videoInputDevicesRef.current, ...audioOutputDevicesRef.current]) {
        if (d.deviceId && d.label) deviceLabelMap[d.deviceId] = d.label;
      }
      const labelFor = (id: string | null) => (id ? deviceLabelMap[id] ?? id : 'default');

      const validated = await validateSelectedDeviceIds(
        selectedAudioInputDevice,
        selectedVideoInputDevice,
        selectedAudioOutputDevice
      );
      if (selectedAudioInputDevice && validated.audio === null) {
        logMeetingEvent({
          ...deviceEventCtx(),
          event_type: 'device_start_fallback',
          device_type: 'audio_input',
          original_device_id: selectedAudioInputDevice,
          original_label: labelFor(selectedAudioInputDevice),
          fallback: 'default',
          attempted_strategy: 'preflight_cleared_stale_id',
        });
      }
      if (selectedVideoInputDevice && validated.video === null) {
        logMeetingEvent({
          ...deviceEventCtx(),
          event_type: 'device_start_fallback',
          device_type: 'video_input',
          original_device_id: selectedVideoInputDevice,
          original_label: labelFor(selectedVideoInputDevice),
          fallback: 'default',
          attempted_strategy: 'preflight_cleared_stale_id',
        });
      }
      if (selectedAudioOutputDevice && validated.audioOutput === null) {
        logMeetingEvent({
          ...deviceEventCtx(),
          event_type: 'device_start_fallback',
          device_type: 'audio_output',
          original_device_id: selectedAudioOutputDevice,
          original_label: labelFor(selectedAudioOutputDevice),
          fallback: 'default',
          attempted_strategy: 'preflight_cleared_stale_id',
        });
      }
      const audioInputId = validated.audio;
      const videoInputId = validated.video;
      const audioOutputId = validated.audioOutput;

      // Collect any device-start failure where BOTH the selected device AND
      // the 'default' fallback failed. Camera/mic failures land on the
      // access-error surface; speaker (audio_output) failures land on a
      // non-blocking toast so the user can still join.
      type FullDeviceFailure = {
        device_type: 'audio_input' | 'video_input' | 'audio_output';
        error_name?: string;
        error_message?: string;
      };
      const deviceFailures: FullDeviceFailure[] = [];

      // Start audio input: fall back to 'default' for iOS browsers (e.g.
      // DuckDuckGo) that return empty deviceIds
      try {
        await meetingManager.startAudioInputDevice(audioInputId || 'default');
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_initial', device_type: 'audio_input', device_id: audioInputId || 'default', device_label: labelFor(audioInputId) });
      } catch (e) {
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_failed', device_type: 'audio_input', device_id: selectedAudioInputDevice, device_label: labelFor(selectedAudioInputDevice), error_name: (e as Error)?.name, error_message: (e as Error)?.message });
        console.warn('[join] Audio input start failed, retrying with default:', e);
        try {
          await meetingManager.startAudioInputDevice('default');
          logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_fallback', device_type: 'audio_input', original_device_id: selectedAudioInputDevice, original_label: labelFor(selectedAudioInputDevice), fallback: 'default' });
        } catch (e2) {
          logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_fallback_failed', device_type: 'audio_input', selected_device_id: selectedAudioInputDevice, selected_label: labelFor(selectedAudioInputDevice), attempted_device_id: 'default', error_name: (e2 as Error)?.name, error_message: (e2 as Error)?.message });
          console.warn('[join] Audio input fallback also failed:', e2);
          deviceFailures.push({ device_type: 'audio_input', error_name: (e2 as Error)?.name, error_message: (e2 as Error)?.message });
        }
      }
      // Start video: try selected device, fall back to 'default' for iOS
      // browsers (e.g. DuckDuckGo) that return empty deviceIds. On
      // OverconstrainedError, retry at 640x480 before the 'default' fallback;
      // see the relaxation block below.
      try {
        await meetingManager.startVideoInputDevice(videoInputId || 'default');
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_initial', device_type: 'video_input', device_id: videoInputId || 'default', device_label: labelFor(videoInputId) });
      } catch (e) {
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_failed', device_type: 'video_input', device_id: selectedVideoInputDevice, device_label: labelFor(selectedVideoInputDevice), error_name: (e as Error)?.name, error_message: (e as Error)?.message });
        console.warn('[join] Video input start failed:', e);

        let relaxedSucceeded = false;
        if ((e as Error)?.name === 'OverconstrainedError') {
          const audioVideo = meetingManager.meetingSession?.audioVideo;
          if (audioVideo) {
            try {
              audioVideo.chooseVideoInputQuality(
                VIDEO_QUALITY_RELAXED.width,
                VIDEO_QUALITY_RELAXED.height,
                VIDEO_QUALITY_RELAXED.frameRate
              );
              await meetingManager.startVideoInputDevice(videoInputId || 'default');
              logMeetingEvent({
                ...deviceEventCtx(),
                event_type: 'device_start_fallback',
                device_type: 'video_input',
                original_device_id: selectedVideoInputDevice,
                original_label: labelFor(selectedVideoInputDevice),
                fallback: 'relaxed_constraints',
                attempted_strategy: 'relaxed_constraints',
              });
              relaxedSucceeded = true;
            } catch (relaxedErr) {
              logMeetingEvent({
                ...deviceEventCtx(),
                event_type: 'device_start_fallback_failed',
                device_type: 'video_input',
                selected_device_id: selectedVideoInputDevice,
                selected_label: labelFor(selectedVideoInputDevice),
                attempted_device_id: videoInputId || 'default',
                attempted_strategy: 'relaxed_constraints',
                error_name: (relaxedErr as Error)?.name,
                error_message: (relaxedErr as Error)?.message,
              });
            } finally {
              // Restore policy unconditionally so a mid-call camera switch via
              // VideoInputTransformControl attempts HD on a capable device.
              // The currently-running track is unaffected — track constraints
              // are immutable post-negotiation.
              audioVideo.chooseVideoInputQuality(
                VIDEO_QUALITY.width,
                VIDEO_QUALITY.height,
                VIDEO_QUALITY.frameRate
              );
            }
          } else {
            console.warn('[join] OverconstrainedError but audioVideo facade unavailable; skipping relaxation');
            logMeetingEvent({
              ...deviceEventCtx(),
              event_type: 'device_start_fallback_failed',
              device_type: 'video_input',
              selected_device_id: selectedVideoInputDevice,
              selected_label: labelFor(selectedVideoInputDevice),
              attempted_device_id: videoInputId || 'default',
              attempted_strategy: 'relaxed_constraints',
              error_name: 'FacadeUnavailable',
              error_message: 'audioVideo facade was null; constraint relaxation skipped',
            });
          }
        }

        if (!relaxedSucceeded) {
          try {
            await meetingManager.startVideoInputDevice('default');
            logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_fallback', device_type: 'video_input', original_device_id: selectedVideoInputDevice, original_label: labelFor(selectedVideoInputDevice), fallback: 'default' });
          } catch (e2) {
            logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_fallback_failed', device_type: 'video_input', selected_device_id: selectedVideoInputDevice, selected_label: labelFor(selectedVideoInputDevice), attempted_device_id: 'default', error_name: (e2 as Error)?.name, error_message: (e2 as Error)?.message });
            console.warn('[join] Video input fallback also failed:', e2);
            deviceFailures.push({ device_type: 'video_input', error_name: (e2 as Error)?.name, error_message: (e2 as Error)?.message });
          }
        }
      }
      // Start audio output: same fallback pattern
      try {
        await meetingManager.startAudioOutputDevice(audioOutputId || 'default');
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_initial', device_type: 'audio_output', device_id: audioOutputId || 'default', device_label: labelFor(audioOutputId) });
      } catch (e) {
        logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_failed', device_type: 'audio_output', device_id: selectedAudioOutputDevice, device_label: labelFor(selectedAudioOutputDevice), error_name: (e as Error)?.name, error_message: (e as Error)?.message });
        console.warn('[join] Audio output start failed, retrying with default:', e);
        try {
          await meetingManager.startAudioOutputDevice('default');
          logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_fallback', device_type: 'audio_output', original_device_id: selectedAudioOutputDevice, original_label: labelFor(selectedAudioOutputDevice), fallback: 'default' });
        } catch (e2) {
          logMeetingEvent({ ...deviceEventCtx(), event_type: 'device_start_fallback_failed', device_type: 'audio_output', selected_device_id: selectedAudioOutputDevice, selected_label: labelFor(selectedAudioOutputDevice), attempted_device_id: 'default', error_name: (e2 as Error)?.name, error_message: (e2 as Error)?.message });
          console.warn('[join] Audio output fallback also failed:', e2);
          deviceFailures.push({ device_type: 'audio_output', error_name: (e2 as Error)?.name, error_message: (e2 as Error)?.message });
        }
      }

      const inputFailures: DeviceStartFailure[] = deviceFailures
        .filter((f) => f.device_type !== 'audio_output')
        .map((f) => ({
          device_type: f.device_type as 'audio_input' | 'video_input',
          error_name: f.error_name,
          error_message: f.error_message,
        }));
      const hasOutputFailure = deviceFailures.some((f) => f.device_type === 'audio_output');

      if (hasOutputFailure) {
        toast(
          "Couldn't start your speaker. Audio playback may not work — try switching speakers in the meeting controls.",
          { duration: 8000 }
        );
      }

      // Tell the encoder to drop framerate before resolution under bandwidth
      // pressure. Pairs with the raised Hi-layer ceiling (set on the simulcast
      // uplink policy in joinChimeSession): at the configured bitrate the
      // encoder produces 720p, and if the link can't sustain it, we'd rather
      // lose fps than pixels.
      //
      // degradationPreference is a top-level RTCRtpSender parameter (not in
      // the encoding map), so it survives the simulcast policy's resubscribe
      // clobber. setParameters can reject on older Safari versions that don't
      // accept the 'maintain-resolution' enum value — non-fatal via .catch();
      // encoder defaults to 'maintain-framerate', which is the existing
      // behavior. The Chime SDK doesn't expose RTCPeerConnection on its
      // public AudioVideoFacade type — the controller's rtcPeerConnection
      // getter is the access path. If the SDK's internal layout shifts,
      // optional chains return undefined and nothing fires.
      const av = meetingManager.meetingSession?.audioVideo as unknown as
        | { audioVideoController?: { rtcPeerConnection?: RTCPeerConnection | null } }
        | undefined;
      const pc = av?.audioVideoController?.rtcPeerConnection;
      const videoSender = pc?.getSenders?.().find((s) => s.track?.kind === "video");
      if (videoSender) {
        const params = videoSender.getParameters();
        videoSender
          .setParameters({ ...params, degradationPreference: "maintain-resolution" })
          .catch(() => {
            // Older Safari / sender not yet bound / SDK access shift — non-fatal.
          });
      }

      // Update store state
      if (chimeMeetingId !== meetingId) {
        setMeetingId(chimeMeetingId);
      }
      if (joinResponse.room_id) {
        setRoomId(joinResponse.room_id);
      }

      // Store the external user ID for multi-tab leave safety
      const externalUserId = joinResponse.join_info?.Attendee?.ExternalUserId || null;
      if (externalUserId) {
        setChimeExternalUserId(externalUserId);
      }

      // Store room members (slice handles array-to-record conversion)
      if (joinResponse.room_members) {
        setRoomMembersData(joinResponse.room_members);
      }

      // Persist session info to sessionStorage (not Zustand persist) so
      // clearExistingSession can clean up after a page refresh. We use
      // sessionStorage rather than the Zustand persist layer because:
      // - sessionStorage auto-clears on tab close (correct lifecycle)
      // - Zustand persist only allows user/authToken; adding video state
      //   would rehydrate stale meeting IDs on every app load
      sessionStorage.setItem(
        "video_session",
        JSON.stringify({
          roomId: joinResponse.room_id,
          meetingId: chimeMeetingId,
          chimeExternalUserId: externalUserId,
        })
      );

      setInitialized(true);

      // Release the camera input that device-init bound above. The lobby has
      // no "join with video on" intent — the user enables video explicitly
      // via the in-meeting toggle — so leaving the input bound would keep
      // the camera light on for the entire meeting. The intro dialog used
      // to do this on close; with the dialog removed, the join flow owns
      // the cleanup. Best-effort: a failure here is non-fatal.
      try {
        await meetingManager.audioVideo?.stopVideoInput();
      } catch {
        // ignore
      }

      // Reset iPhone-mic-alert state after successful join. By this point,
      // device-start has run and the slice's selectedAudioInputDevice
      // reflects the final post-fallback state. Clearing both the pending
      // alert and the user-explicit-iPhone flag means an in-meeting
      // Continuity reroute can re-prompt even if the lobby was dismissed —
      // and an explicit lobby choice of iPhone is re-evaluated in-meeting.
      usePlatformStore.getState().resetIPhoneMicAlertState();

      if (user) {
        setUserName(`${user.first_name} ${user.last_name}`.trim() || null);
      }

      if (chimeMeetingId && user?.id) {
        videoEventBus.emitEvent("videochat:joined", {
          meetingId: chimeMeetingId,
        });
      }

      // Surface device-start failures via the access-error dialog WITHOUT
      // tearing down the SDK or undoing the join state. The Chime session
      // is alive, sessionStorage holds the session info, and store fields
      // (meetingId, externalUserId, roomMembers) are populated. This means:
      //   - Continue (dialog primary) just navigates to the meeting page;
      //     no re-join needed because the SDK is already joined.
      //   - Cancel (dialog secondary) calls leaveMeeting() which tears
      //     down the SDK + clears stores + clears sessionStorage.
      //
      // The sentinel throw still propagates up so handleJoin doesn't fire
      // the COMPLETED engine-action transition; that fires from the
      // dialog's Continue handler instead.
      if (inputFailures.length > 0) {
        const { title, summary, causes } = getDeviceStartErrorGuidance(inputFailures);
        setSessionError(summary, { title, causes });
        throw new DeviceStartFailureHandled();
      }

      // Navigate to meeting page
      navigateRef.current(`/video/meeting/${meetingDetails.id}`);
    } catch (err) {
      // Sentinel: device-start failure already wrote sessionError +
      // sessionErrorDetail above. Re-throw so handleJoin / MeetingJoinDetails
      // skip their respective COMPLETED transition and dialog dispatch.
      if (err instanceof DeviceStartFailureHandled) {
        throw err;
      }
      console.error("[useJoinVideoMeeting] Join failed:", err);
      setSessionError(err instanceof Error ? err.message : String(err));
      setInitialized(false);
    }
  }, [
    meetingId,
    meetingDetails,
    region,
    enableSimulcast,
    priorityBasedPolicy,
    keepLastFrameWhenPaused,
    isEchoReductionEnabled,
    meetingManager,
    selectedAudioInputDevice,
    selectedVideoInputDevice,
    selectedAudioOutputDevice,
    setMeetingId,
    setRoomId,
    setInitialized,
    setRegion,
    setMeetingMode,
    setSessionError,
    setRoomMembersData,
    setChimeExternalUserId,
    setUserName,
    user,
  ]);

  // Leave meeting: cleanup streams + API + Chime leave
  const leaveMeeting = useCallback(async () => {
    try {
      setSessionError(null);

      // Send camera-off intent BEFORE teardown so remote participants
      // can use the short roster grace (ROSTER_LEAVE_GRACE_MS).
      // This must be imperative, not via React effects, because the
      // data channel closes during leaveChimeSession().
      try {
        const audioVideo = meetingManager.meetingSession?.audioVideo;
        if (audioVideo) {
          audioVideo.realtimeSendDataMessage(
            "device-state",
            JSON.stringify({
              camera: false,
              mic: false,
              timestamp: Date.now(),
            }),
            30_000
          );
          // Small delay to let the data message reach the SFU before teardown
          await new Promise((r) => setTimeout(r, 100));
        }
      } catch {
        // Best-effort — if this fails, remote will use 30s grace
      }

      await stopChimeStreams(meetingManager);

      await performVideoLeave({
        roomId: roomId ?? null,
        meetingId: meetingId ?? null,
        chimeExternalUserId: chimeExternalUserId ?? null,
        meetingManager,
      });

      resetVideo();
      resetVideoMeeting();
      sessionStorage.removeItem("video_session");
    } catch (err) {
      const errorMessage = (err as Error).message;
      setSessionError(errorMessage);
      resetVideo();
      resetVideoMeeting();
      sessionStorage.removeItem("video_session");
      throw err;
    }
  }, [
    meetingId,
    roomId,
    chimeExternalUserId,
    meetingManager,
    setSessionError,
    resetVideo,
    resetVideoMeeting,
  ]);

  // Clear existing session (used when revisiting join page).
  // After a page refresh, Zustand video state resets to null/false,
  // so we fall back to sessionStorage for roomId/meetingId to ensure
  // backend leave API and Chime cleanup still run.
  const clearExistingSession = useCallback(async () => {
    if (inFlightClear) return inFlightClear;

    inFlightClear = (async () => {
      const stored = sessionStorage.getItem("video_session");
      let fallback = null;
      try { fallback = stored ? JSON.parse(stored) : null; } catch {}
      const effectiveRoomId = roomId ?? fallback?.roomId;
      const effectiveMeetingId = meetingId ?? fallback?.meetingId;
      const effectiveExternalUserId = chimeExternalUserId ?? fallback?.chimeExternalUserId;

      if (!effectiveRoomId && !effectiveMeetingId) return;

      try {
        if (effectiveRoomId) {
          try {
            await leaveVideoMeetingApi(effectiveRoomId, effectiveExternalUserId);
          } catch {
            // Non-fatal
          }
        }
        if (effectiveMeetingId) {
          await leaveChimeSession(meetingManager);
        }
      } catch {
        // Silently handle cleanup errors
      }

      sessionStorage.removeItem("video_session");
      setInitialized(false);
    })();

    try {
      await inFlightClear;
    } finally {
      inFlightClear = null;
    }
  }, [meetingId, roomId, chimeExternalUserId, meetingManager, setInitialized]);

  return {
    meetingManager,
    joinMeeting,
    leaveMeeting,
    clearExistingSession,
  };
}
